// =============================================================================
// relance-journal-client.ts — Client API du JOURNAL DES RELANCES admin (BC campaign), DOUBLABLE.
// =============================================================================
// R15-COM-05 / DBU §2.7.1 (l.1526 « Filtres et tri ») — miroir de l'endpoint ADMIN
// GET /api/admin/campaigns/:campaignId/relances (AdminSessionGuard). Comme les autres clients UI, expose
// une INTERFACE consommée par un hook TanStack ; l'impl HTTP réelle (fetch) est substituable via le DI seam
// (RelanceJournalClientProvider) sans toucher l'UI (stub-first).
//
// AUTORITÉ = API : le journal est un read-model ADMIN qui AGRÈGE des relances de PLUSIEURS fournisseurs. Il
// ne porte QUE des identifiants techniques (supplierSiret + destinataireContactId LOGIQUE) — JAMAIS de nom
// nominatif de contact (SECOPS : pas de fabrication de nom ; le SIRET est l'identité de l'établissement
// destinataire, présentée en CADRAGE CCI). L'UI LIT et habille : aucune règle métier ici.
//
// DTO = miroirs HTTP (PAS le Published Language, PAS @jeko/db) : `type` est répliqué en union de littéraux
// ASCII (parité back) — la SPA ne couple pas au paquet DB.

import { HttpApiError } from '@/api/http-error';

/** Type de relance (parité back). Individuelle = 1 SIRET ; Groupée = cohorte ; Automatique = échéance système. */
export type RelanceTypeDto = 'Individuelle' | 'Groupee' | 'Automatique';

/** Canal d'émission de la relance. Seule valeur émise en V1 (Postmark). Miroir HTTP. */
export type RelanceCanalDto = 'Email';

/**
 * Une entrée du journal des relances (grain : un envoi vers un contact d'un établissement). Miroir HTTP.
 * `emisParAdmin` = null pour une relance AUTOMATIQUE (aucun acteur humain). `destinataireContactId` est un
 * identifiant LOGIQUE (pas un nom) ; l'identité présentée du destinataire est le `supplierSiret`.
 */
export interface RelanceJournalEntryDto {
  readonly relanceId: string;
  readonly campaignId: string;
  readonly supplierSiret: string;
  readonly destinataireContactId: string;
  readonly emisParAdmin: string | null;
  readonly type: RelanceTypeDto;
  readonly canal: RelanceCanalDto;
  /** Date d'envoi (ISO 8601). */
  readonly sentAt: string;
}

/** Réponse du journal : campagne + entrées (vide → `entries: []`). Miroir HTTP. */
export interface RelanceJournalResponseDto {
  readonly campaignId: string;
  readonly entries: readonly RelanceJournalEntryDto[];
}

/** Erreur du client Journal des relances (conserve le statut HTTP — intercepteur 401 admin). */
export class RelanceJournalApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'RelanceJournalApiError');
  }
}

/** Interface DOUBLABLE : une seule lecture admin (aucune mutation — read-model). */
export interface RelanceJournalClient {
  fetchJournal(campaignId: string): Promise<RelanceJournalResponseDto>;
}
